/**
 * External dependencies
 */
import * as React from 'react';
import styled from 'styled-components';
import { CSSTransition } from 'react-transition-group';
import type { ReactNode } from 'react';
import {
  useCallback,
  useFocusOut,
  useRef,
  useState,
  useUnmount,
} from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import {
  Button,
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
  Icons,
  PLACEMENT,
  useKeyDownEffect,
} from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import Tooltip from '../../../tooltip';
import Popup from '../../../popup';
import useInspector from '../../../inspector/useInspector';
import useFocusTrapping from '../../../../utils/useFocusTrapping';
import { focusStyle } from '../../shared';
import { TEXT_BORDER_SETTINGS_POPUP_ID } from './shared';

const SettingsButton = styled(Button).attrs({
  size: BUTTON_SIZES.SMALL,
  type: BUTTON_TYPES.TERTIARY,
  variant: BUTTON_VARIANTS.SQUARE,
})`
  ${focusStyle};
`;

export function TextBorderSettings({ children }: { children: ReactNode }) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const buttonRef = useRef(null);

  const {
    refs: { inspector },
  } = useInspector();

  const onSettingsClose = useCallback(() => {
    setSettingsOpen(false);
  }, [setSettingsOpen]);

  // Always hide color picker on unmount - note the double arrows
  useUnmount(() => () => onSettingsClose());

  return (
    <>
      <Tooltip title={__('Stroke Settings', 'web-stories')} hasTail>
        <SettingsButton
          aria-label={__('Stroke Settings', 'web-stories')}
          onClick={() => setSettingsOpen(true)}
          ref={buttonRef}
        >
          <Icons.Settings />
        </SettingsButton>
      </Tooltip>
      <Popup
        anchor={buttonRef}
        dock={inspector}
        isOpen={settingsOpen}
        placement={PLACEMENT.LEFT_START}
        spacing={{ x: 20 }}
        invisible={false}
        renderContents={({ propagateDimensionChange }) => (
          <TextBorderSettingsPopup onClose={onSettingsClose}>
            {children}
          </TextBorderSettingsPopup>
        )}
      />
    </>
  );
}

const Container = styled.div`
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bg.secondary};
  width: 256px;
  user-select: none;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
  padding: 16px;

  &.picker-appear {
    opacity: 0.01;
    margin-top: -10px;

    &.picker-appear-active {
      opacity: 1;
      margin-top: 0;
      transition: 300ms ease-out;
      transition-property: opacity, margin-top;
    }
  }
`;

function TextBorderSettingsPopup({
  children,
  onClose,
}: {
  children: ReactNode;
  onClose: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>();

  // Re-establish focus when actively exiting by button or key press
  const previousFocus = useRef(document.activeElement);
  const handleCloseAndRefocus = useCallback(() => {
    // Ignore reason: In Jest, focus is always on document.body if not on any specific
    // element, so it can never be falsy, as it can be in a real browser.

    // istanbul ignore else
    if (previousFocus.current) {
      previousFocus.current.focus();
    }
    onClose();
  }, [onClose]);

  useFocusOut(
    containerRef,
    (event) => {
      // The focus out event isn't reliable when the popup includes a dropdown (since the dropdown elements are outside of the popup in the DOM) so we need to do some manual checking
      const target = event.target as HTMLElement;
      const targetIsInsidePopup = containerRef.current?.contains(target);

      if (targetIsInsidePopup) {
        return;
      }

      const targetIsInsideDropdown = target.closest(
        `#${TEXT_BORDER_SETTINGS_POPUP_ID}`
      );

      if (!targetIsInsideDropdown) {
        onClose();
      }
    },
    [onClose]
  );
  useKeyDownEffect(containerRef, 'esc', handleCloseAndRefocus);
  useFocusTrapping({ ref: containerRef });

  return (
    <CSSTransition in appear classNames="picker" timeout={300}>
      <Container
        role="dialog"
        aria-label={__('Text stroke settings', 'web-stories')}
        ref={containerRef}
      >
        {children}
      </Container>
    </CSSTransition>
  );
}
